import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Globe, ChevronRight, ShieldCheck, Mail, MapPin, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_compliance-hub-324/artifacts/fbkf58xb_ChatGPT%20Image%20Feb%209%2C%202026%2C%2010_13_57%20PM.png";
const LOGO_SQUARE = "https://customer-assets.emergentagent.com/job_compliance-hub-324/artifacts/wt8k58us_ChatGPT%20Image%20Feb%209%2C%202026%2C%2010_13_51%20PM.png";

export const Header = () => {
  const { language, toggleLanguage, t, dir } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { to: '/', label: t.nav.home },
    { to: '/services', label: t.nav.services },
    { to: '/about', label: t.nav.about },
    { to: '/contact', label: t.nav.contact }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-xl shadow-lg' 
          : 'bg-white/80 backdrop-blur-md'
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="SafePro Global Consultancy" 
              className="h-20 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`relative text-sm font-medium uppercase tracking-wider transition-colors duration-200 ${
                  isActive(link.to) 
                    ? 'text-primary' 
                    : 'text-muted-foreground hover:text-primary'
                }`}
                data-testid={`nav-link-${link.to.replace('/', '') || 'home'}`}
              >
                {link.label}
                {isActive(link.to) && (
                  <motion.div
                    layoutId="activeNav"
                    className="absolute -bottom-1 left-0 right-0 h-0.5 bg-accent"
                  />
                )}
              </Link>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-4">
            {/* SafeProG Platform Link */}
            <a
              href="https://app.safeproglobal.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold uppercase tracking-wide text-white bg-secondary hover:bg-secondary/90 transition-colors duration-200"
              data-testid="platform-link"
            >
              <ExternalLink className="w-4 h-4" />
              {t.nav.platform}
            </a>

            {/* Language Switcher */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors duration-200 border border-border rounded-none hover:border-primary"
              data-testid="language-switcher"
            >
              <Globe className="w-4 h-4" />
              <span>{language === 'en' ? 'العربية' : 'English'}</span>
            </button>

            {/* CTA Button */}
            <Link to="/contact" className="hidden md:block">
              <Button 
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none px-6 font-semibold uppercase tracking-wide text-sm"
                data-testid="header-cta"
              >
                {t.nav.getQuote}
              </Button>
            </Link>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-primary"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-border"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col py-4 px-6">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`py-3 text-base font-medium border-b border-border/50 ${
                    isActive(link.to) ? 'text-primary' : 'text-muted-foreground'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link 
                to="/contact" 
                onClick={() => setMobileMenuOpen(false)}
                className="mt-4"
              >
                <Button className="w-full bg-primary text-primary-foreground rounded-none">
                  {t.nav.getQuote}
                </Button>
              </Link>
              <a
                href="https://app.safeproglobal.com"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold uppercase tracking-wide text-white bg-secondary hover:bg-secondary/90 transition-colors duration-200"
                data-testid="mobile-platform-link"
              >
                <ExternalLink className="w-4 h-4" />
                {t.nav.platform}
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export const Footer = () => {
  const { t, language } = useLanguage();

  const services = [
    { label: language === 'en' ? 'EHS Consultancy' : 'استشارات الصحة والسلامة والبيئة', to: '/services#ehs' },
    { label: language === 'en' ? 'Risk Management' : 'إدارة المخاطر', to: '/services#risk' },
    { label: language === 'en' ? 'Project Management' : 'إدارة المشاريع', to: '/services#pmo' },
    { label: language === 'en' ? 'Payroll Services' : 'خدمات الرواتب', to: '/services#payroll' },
    { label: language === 'en' ? 'IT Consulting' : 'استشارات تكنولوجيا المعلومات', to: '/services#it' }
  ];

  const quickLinks = [
    { label: t.nav.home, to: '/' },
    { label: t.nav.services, to: '/services' },
    { label: t.nav.about, to: '/about' },
    { label: t.nav.contact, to: '/contact' }
  ];

  return (
    <footer className="bg-primary text-primary-foreground" data-testid="footer">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <img 
              src={LOGO_SQUARE} 
              alt="SafePro Global" 
              className="h-20 w-20 mb-6 bg-white p-2 rounded"
            />
            <p className="text-primary-foreground/80 text-sm leading-relaxed">
              {t.footer.description}
            </p>
            <div className="flex items-center gap-2 mt-6">
              <ShieldCheck className="w-5 h-5 text-secondary" />
              <span className="text-sm font-medium">ISO 45001 & ISO 14001 Aligned</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6 uppercase tracking-wide">{t.footer.quickLinks}</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.to}>
                  <Link 
                    to={link.to}
                    className="text-primary-foreground/80 hover:text-secondary transition-colors duration-200 flex items-center gap-2 text-sm"
                  >
                    <ChevronRight className="w-4 h-4" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-6 uppercase tracking-wide">{t.footer.services}</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.to}>
                  <Link 
                    to={service.to}
                    className="text-primary-foreground/80 hover:text-secondary transition-colors duration-200 flex items-center gap-2 text-sm"
                  >
                    <ChevronRight className="w-4 h-4" />
                    {service.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-6 uppercase tracking-wide">{t.footer.contact}</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                <span className="text-primary-foreground/80 text-sm">{t.contact.info.locationValue}</span>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                <a 
                  href="mailto:info@safeproglobal.com"
                  className="text-primary-foreground/80 hover:text-secondary transition-colors duration-200 text-sm"
                >
                  {t.contact.info.emailValue}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <ExternalLink className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                <a 
                  href="https://www.safeproglobal.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-foreground/80 hover:text-secondary transition-colors duration-200 text-sm"
                >
                  {t.contact.info.websiteValue}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-primary-foreground/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <p className="text-center text-primary-foreground/60 text-sm">
            {t.footer.rights}
          </p>
        </div>
      </div>
    </footer>
  );
};

export const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-20">
        {children}
      </main>
      <Footer />
    </div>
  );
};
